import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { startAuthentication, startRegistration, browserSupportsWebAuthnAutofill, WebAuthnAbortService } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { Me, RecoveryCodes } from '@pen/shared';
import { request } from '../api';
import { message } from '../ui/hooks';
import { useSession } from './session';

export function AuthPage({ mode }: { mode: 'login' | 'register' | 'recover' }) {
  const [search] = useSearchParams();
  const [handle, setHandle] = useState('');
  const [invite, setInvite] = useState(search.get('invite') ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const session = useSession();
  const navigate = useNavigate();
  const manual = useRef(false);
  const conditionalOptions = useRef<Promise<PublicKeyCredentialRequestOptionsJSON> | null>(null);
  useEffect(() => {
    manual.current = false;
    if (mode !== 'login') return;
    let live = true;
    void (async () => {
      if (!await browserSupportsWebAuthnAutofill() || !live || manual.current) return;
      conditionalOptions.current = request<PublicKeyCredentialRequestOptionsJSON>('POST', '/api/auth/login/options', {});
      const options = await conditionalOptions.current;
      if (!live || manual.current) return;
      const response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
      if (!live || manual.current) return;
      session.setUser(await request('POST', '/api/auth/login/verify', response, Me));
      navigate('/', { replace: true });
    })().catch(() => { /* Conditional login is optional; the explicit button remains available. */ });
    return () => { live = false; WebAuthnAbortService.cancelCeremony(); };
  }, [mode]);
  if (session.user && !codes) return <Navigate to="/" replace />;
  async function submit() {
    manual.current = true;
    setError(null); setBusy(true); WebAuthnAbortService.cancelCeremony();
    try {
      // Finish an outstanding autofill-options request before issuing a new cookie-bound challenge.
      await conditionalOptions.current?.catch(() => undefined);
      if (mode === 'login') {
        const options = await request<PublicKeyCredentialRequestOptionsJSON>('POST', '/api/auth/login/options', handle ? { handle } : {});
        const response = await startAuthentication({ optionsJSON: options });
        session.setUser(await request('POST', '/api/auth/login/verify', response, Me));
        navigate('/', { replace: true });
      } else {
        const options = await request<PublicKeyCredentialCreationOptionsJSON>('POST', mode === 'register' ? '/api/auth/register/options' : '/api/auth/recover', mode === 'register' ? { handle, invite } : { handle, code });
        const response = await startRegistration({ optionsJSON: options });
        const result: unknown = await request('POST', '/api/auth/register/verify', response);
        session.setUser(Me.parse(result));
        if (mode === 'register') setCodes(RecoveryCodes.parse(result).codes);
        else navigate('/', { replace: true });
      }
    } catch (reason) { setError(message(reason)); }
    finally { setBusy(false); }
  }
  if (codes) return <main className="auth-page"><p className="kicker">One important thing</p><h1>Keep a way back in.</h1><p>Save these recovery codes somewhere private, outside this device. Each works once if you lose your passkeys. We cannot show them again.</p><div className="recovery-codes">{codes.map(value => <code key={value}>{value}</code>)}</div><p className="muted">Treat these like passwords. Do not share them.</p><button onClick={() => navigate('/', { replace: true })}>I’ve saved my codes →</button></main>;
  const title = mode === 'login' ? 'Your place to think.' : mode === 'register' ? 'Start a notebook.' : 'A way back in.';
  return <main className="auth-page"><p className="kicker">Pen studio</p><h1>{title}</h1><p className="muted">{mode === 'login' ? 'A lesson, a blank page, and room to figure it out. Sign in with your passkey.' : mode === 'register' ? 'Create an account with your invitation and a passkey. No password to remember.' : 'Use a saved recovery code to create a new passkey.'}</p>
    <form className="form-stack" onSubmit={event => { event.preventDefault(); void submit(); }}>
      <label>Handle {mode === 'login' && <small>Optional — your passkey can find your account</small>}<input name="username" autoComplete={mode === 'login' ? 'username webauthn' : 'username'} autoCapitalize="none" autoCorrect="off" pattern="[a-z0-9_]{2,24}" value={handle} onChange={event => setHandle(event.target.value)} required={mode !== 'login'} placeholder="your_handle" /></label>
      {mode === 'register' && <label>Invitation code<input value={invite} onChange={event => setInvite(event.target.value)} required minLength={8} autoComplete="off" /></label>}
      {mode === 'recover' && <label>Recovery code<input value={code} onChange={event => setCode(event.target.value)} required autoComplete="off" /></label>}
      {error && <p role="alert" className="error">{error}</p>}
      <button disabled={busy}>{busy ? 'Waiting for your passkey…' : mode === 'login' ? 'Sign in with a passkey' : 'Create a passkey'}</button>
    </form>
    <div className="actions">{mode !== 'login' && <Link to="/login">Back to sign in</Link>}{mode === 'login' && <><Link to="/register">Have an invitation?</Link><Link to="/recover">Lost your passkey?</Link></>}</div>
  </main>;
}
